import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchAdminOpsSettings,
  updateAdminOpsSettings,
  type AdminOpsSettingsInput,
} from "@/features/admin/api/admin-ops.api";

/**
 * Read on every admin tab (the Events and Scheduled jobs tabs take their
 * defaults from it), written on one — so it can sit as long as the changelog
 * settings do.
 */
const ADMIN_OPS_SETTINGS_STALE_TIME_MS = 5 * 60 * 1000;

export const adminOpsKeys = {
  settings: ["admin-ops", "settings"] as const,
} as const;

export function adminOpsSettingsQueryOptions() {
  return queryOptions({
    queryKey: adminOpsKeys.settings,
    queryFn: fetchAdminOpsSettings,
    staleTime: ADMIN_OPS_SETTINGS_STALE_TIME_MS,
  });
}

/**
 * Saves the operations settings and seeds the cache with the server's copy,
 * so the Events and Scheduled jobs tabs pick the new defaults up without a
 * refetch. Errors surface next to the save button, not as a global toast.
 */
export function useSaveAdminOpsSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Partial<AdminOpsSettingsInput>) => updateAdminOpsSettings(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(adminOpsKeys.settings, settings);
    },
    meta: { skipGlobalError: true },
  });
}
